/**
 * usePopupTableActions - Table actions hook for popup windows
 *
 * This hook mirrors the logic from useTableActions but sends events
 * to the main window via PopupActions instead of directly manipulating
 * the map/context.
 */
import { useCallback, useMemo } from 'react';
import { useCoordinateExtraction } from '../Reports/Table/hooks';
import { RowAction } from '../Reports/Table/hooks';
import {
  normalizeRowForTarget,
  hasDualMMSI,
  getMMSI_A,
  getMMSI_B,
  getName_A,
  getName_B,
} from '../Reports/Table/hooks/rowUtils';
import { PopupActions } from './PopupEvents';

// Re-export RowAction type for convenience
export type { RowAction } from '../Reports/Table/hooks';

export interface UsePopupTableActionsProps {
  shipData: any[] | undefined;
}

/**
 * Hook for generating table row actions in popup windows
 *
 * Actions send events to the main window instead of directly
 * manipulating the map.
 */
export const usePopupTableActions = ({
  shipData,
}: UsePopupTableActionsProps) => {
  const { extractCoordinates, hasPositionData } = useCoordinateExtraction();

  const sendViewTargetEvent = useCallback((mmsi: string, name?: string) => {
    PopupActions.viewTarget({
      mmsi: mmsi,
      name: name || undefined,
    });
  }, []);

  const onGoToTarget = useCallback((row: any) => {
    const normalizedRow = normalizeRowForTarget(row);

    const mmsi = normalizedRow?.mmsi || normalizedRow?.['to mmsi'];
    const name = normalizedRow?.name !== mmsi ? normalizedRow?.name : '';

    PopupActions.viewTarget({
      mmsi: mmsi ? String(mmsi) : undefined,
      name: name || undefined,
      imo: normalizedRow?.imo ? String(normalizedRow.imo) : undefined,
      callSign: normalizedRow?.callSign
        ? String(normalizedRow.callSign)
        : undefined,
    });
  }, []);

  const onZoomToCoordinates = useCallback(
    (row: any) => {
      const coordinates = extractCoordinates(row);

      if (coordinates) {
        const [lon, lat] = coordinates;
        PopupActions.zoomToCoordinates({ lat, lon });
      }
    },
    [extractCoordinates],
  );

  const actions = useMemo((): RowAction[] => {
    if (!shipData || shipData.length === 0) {
      return [];
    }

    const sampleRow = shipData[0];
    const keys = Object.keys(sampleRow);
    const rowActions: RowAction[] = [];

    const isDualMMSI = hasDualMMSI(sampleRow);

    if (isDualMMSI) {
      rowActions.push({
        label: 'View target A',
        onClick: (row) => {
          const mmsi = getMMSI_A(row);
          const name = getName_A(row);
          if (mmsi) {
            sendViewTargetEvent(String(mmsi), name ? String(name) : undefined);
          }
        },
      });
      rowActions.push({
        label: 'View target B',
        onClick: (row) => {
          const mmsi = getMMSI_B(row);
          const name = getName_B(row);
          if (mmsi) {
            sendViewTargetEvent(String(mmsi), name ? String(name) : undefined);
          }
        },
      });
    } else {
      const hasMMSI = keys.some((key) => key.toLowerCase().includes('mmsi'));

      if (hasMMSI) {
        rowActions.push({
          label: 'View target',
          onClick: (row) => {
            onGoToTarget(row);
          },
        });
      }
    }

    const hasPosition = hasPositionData(sampleRow);

    if (hasPosition) {
      rowActions.push({
        label: 'Zoom to event',
        onClick: (row) => {
          onZoomToCoordinates(row);
        },
      });
    }

    return rowActions;
  }, [
    shipData,
    hasPositionData,
    onGoToTarget,
    onZoomToCoordinates,
    sendViewTargetEvent,
  ]);

  const getActionsForRow = useMemo(() => {
    return (row: any): RowAction[] => {
      return actions.filter((action) => {
        if (action.label === 'Zoom to event') {
          return hasPositionData(row);
        }
        return true;
      });
    };
  }, [actions, hasPositionData]);

  const hasActions = actions.length > 0;

  return {
    actions,
    getActionsForRow,
    hasActions,
  };
};
